// Canvas2D-composed text textures (docs/07 §9): crisp, localisable, renderer-agnostic.
import * as THREE from 'three';
import { drawIcon, type IconName } from '../game/icons';
import { drawGlyph } from './cardFaces';

/** Labels render in an overlay pass after post-processing (crisp, never darkened by AO or bloom). */
export const LABEL_LAYER = 1;

export interface PlaqueOptions {
  title: string;
  subtitle?: string;
  width?: number;
  accent?: string;
  dark?: boolean;
}

export function plaqueTexture(o: PlaqueOptions): THREE.CanvasTexture {
  const w = o.width ?? 640;
  const h = o.subtitle ? 190 : 120;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d')!;
  const r = 26;
  g.beginPath();
  g.roundRect(6, 6, w - 12, h - 12, r);
  g.fillStyle = o.dark ? 'rgba(33,24,20,0.92)' : 'rgba(241,229,203,0.96)';
  g.fill();
  g.lineWidth = 6;
  g.strokeStyle = o.accent ?? '#9b7a45';
  g.stroke();
  g.fillStyle = o.dark ? '#f3e7cf' : '#2b2018';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = `700 ${o.subtitle ? 56 : 58}px "Cormorant Garamond", Georgia, serif`;
  fitText(g, o.title, w - 60);
  g.fillText(o.title, w / 2, o.subtitle ? 70 : h / 2 + 2);
  if (o.subtitle) {
    g.font = `500 38px "Alegreya Sans", system-ui, sans-serif`;
    g.fillStyle = o.dark ? '#d8c7a6' : '#5d4a36';
    fitText(g, o.subtitle, w - 60);
    g.fillText(o.subtitle, w / 2, 138);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 16;
  return tex;
}

function fitText(g: CanvasRenderingContext2D, text: string, max: number): void {
  let size = parseInt(/(\d+)px/.exec(g.font)![1]!, 10);
  while (g.measureText(text).width > max && size > 18) {
    size -= 2;
    g.font = g.font.replace(/\d+px/, `${size}px`);
  }
}

/** A camera-facing text sprite sized in world metres. */
export function plaqueSprite(o: PlaqueOptions, worldWidth: number): THREE.Sprite {
  const tex = plaqueTexture(o);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, toneMapped: false });
  const sprite = new THREE.Sprite(mat);
  const aspect = tex.image.height / tex.image.width;
  sprite.scale.set(worldWidth, worldWidth * aspect, 1);
  sprite.layers.set(LABEL_LAYER);
  return sprite;
}

export interface BannerOptions {
  title: string;
  subtitle: string;
  accent: string;
  icon: IconName;
}

/** Room banner (key art, Mockup 1): parchment plaque, dark gilt frame, accent medallion + icon. */
export function roomBannerTexture(o: BannerOptions): THREE.CanvasTexture {
  const w = 1024;
  const h = 240;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  const x0 = 70;
  const pad = 14;
  // Frame: dark lacquer with a gilt inner line.
  g.shadowColor = 'rgba(0,0,0,0.55)';
  g.shadowBlur = 16;
  g.shadowOffsetY = 6;
  g.beginPath();
  g.roundRect(x0, pad + 18, w - x0 - pad, h - 2 * pad - 36, 18);
  g.fillStyle = '#1d140f';
  g.fill();
  g.shadowColor = 'transparent';
  const paper = g.createLinearGradient(0, 0, 0, h);
  paper.addColorStop(0, '#f7ecd2');
  paper.addColorStop(1, '#e2cfa6');
  g.beginPath();
  g.roundRect(x0 + 8, pad + 26, w - x0 - pad - 16, h - 2 * pad - 52, 12);
  g.fillStyle = paper;
  g.fill();
  g.lineWidth = 3;
  g.strokeStyle = '#b48c46';
  g.stroke();
  // Corner flourishes.
  g.strokeStyle = '#8a6a36';
  g.lineWidth = 2;
  for (const [cx, sx] of [
    [x0 + 22, 1],
    [w - pad - 14, -1],
  ] as const) {
    for (const [cy, sy] of [
      [pad + 40, 1],
      [h - pad - 40, -1],
    ] as const) {
      g.beginPath();
      g.moveTo(cx, cy + sy * 18);
      g.quadraticCurveTo(cx, cy, cx + sx * 18, cy);
      g.stroke();
    }
  }
  // Medallion.
  const mx = x0 + 18;
  const my = h / 2;
  const mr = 86;
  g.shadowColor = 'rgba(0,0,0,0.5)';
  g.shadowBlur = 14;
  g.beginPath();
  g.arc(mx, my, mr, 0, Math.PI * 2);
  const ring = g.createLinearGradient(mx - mr, my - mr, mx + mr, my + mr);
  ring.addColorStop(0, '#f3d68c');
  ring.addColorStop(0.5, '#a57a33');
  ring.addColorStop(1, '#f0cf7e');
  g.fillStyle = ring;
  g.fill();
  g.shadowColor = 'transparent';
  g.beginPath();
  g.arc(mx, my, mr - 12, 0, Math.PI * 2);
  const disc = g.createRadialGradient(mx - 20, my - 24, 8, mx, my, mr);
  disc.addColorStop(0, shadeHex(o.accent, 0.35));
  disc.addColorStop(1, shadeHex(o.accent, -0.45));
  g.fillStyle = disc;
  g.fill();
  drawIcon(g, o.icon, mx, my, mr * 1.08, '#fff7e6');
  // Text.
  const tx = (x0 + mr + 18 + w - pad) / 2;
  const maxW = w - pad - (x0 + mr + 40);
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillStyle = '#2a1d14';
  g.font = `700 54px "Cinzel", "Cormorant Garamond", Georgia, serif`;
  const title = o.title.replace(/^Dept\. of/, 'Department of').toUpperCase();
  fitText(g, title, maxW);
  g.fillText(title, tx, h / 2 - 24);
  g.fillStyle = '#6a4e32';
  richLine(g, o.subtitle, tx, h / 2 + 34, 40, maxW, '#6a4e32');
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 16;
  return tex;
}

export function roomBanner(o: BannerOptions, worldWidth: number): THREE.Sprite {
  const tex = roomBannerTexture(o);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, toneMapped: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(worldWidth, (worldWidth * tex.image.height) / tex.image.width, 1);
  sprite.layers.set(LABEL_LAYER);
  return sprite;
}

function shadeHex(hex: string, amt: number): string {
  const c = new THREE.Color(hex);
  c.lerp(new THREE.Color(amt >= 0 ? '#ffffff' : '#000000'), Math.abs(amt));
  return `#${c.getHexString()}`;
}

type Token = { text: string } | { glyph: 'red' | 'yellow' | 'blue' | 'green' | 'mana' | 'vp' };
const GLYPH_TOKENS: Record<string, 'red' | 'yellow' | 'blue' | 'green' | 'mana' | 'vp'> = { R: 'red', Y: 'yellow', B: 'blue', G: 'green', MANA: 'mana', VP: 'vp' };

function tokens(text: string): Token[] {
  const out: Token[] = [];
  for (const part of text.split(/(\{[A-Z]+\})/)) {
    if (!part) continue;
    const m = /^\{([A-Z]+)\}$/.exec(part);
    const glyph = m ? GLYPH_TOKENS[m[1]!] : undefined;
    if (glyph) out.push({ glyph });
    else out.push({ text: part });
  }
  return out;
}

/** One line of rules text with inline permit / mana glyphs, centred and shrunk to fit. */
export function richLine(g: CanvasRenderingContext2D, text: string, cx: number, cy: number, px: number, maxW: number, color: string): void {
  const toks = tokens(text.replace(/\.$/, ''));
  let size = px;
  const measure = () => {
    g.font = `500 ${size}px "Alegreya Sans", system-ui, sans-serif`;
    return toks.reduce((w, t) => w + ('text' in t ? g.measureText(t.text).width : size * 0.95), 0);
  };
  let w = measure();
  while (w > maxW && size > 18) {
    size -= 2;
    w = measure();
  }
  let x = cx - w / 2;
  g.textAlign = 'left';
  g.textBaseline = 'middle';
  g.fillStyle = color;
  for (const t of toks) {
    if ('text' in t) {
      g.fillText(t.text, x, cy);
      x += g.measureText(t.text).width;
    } else {
      drawGlyph(g, t.glyph, x + size * 0.47, cy, size * 0.4);
      x += size * 0.95;
    }
  }
  g.textAlign = 'center';
}
